// Remembering which conversation a chat slot was left on, so a reload lands back on it.
//
// Without this, the chat a view mounts is resolved per browser session: init_web_chat
// hands back the chat tied to `execution_session_id`, which sessionStorage keeps until
// the tab closes. That covers a refresh and nothing else — a new tab opens a new thread,
// and the auto-run of `processName` fires again on every reload, because the ref that
// guards it dies with the page and a second execution starts over the same chat.
//
// `keepState` writes both facts to localStorage instead: which chat the slot resolved to,
// and which auto-run already happened on it. The key is the (flow, slot) pair, because a
// view can mount several chats and each one is its own conversation.
//
// The token is how the app says "this is a different conversation now" — a wizard that
// finished, a record the screen switched to. There is one record per slot either way, so
// a changed token replaces what is stored rather than piling up keys nobody reads.

const PREFIX = "seed_chat_state";

export const getChatStateKey = (flowId, slotId) =>
  flowId ? `${PREFIX}:${flowId}:${slotId || "default"}` : null;

const readRecord = (key) => {

  if (!key) return null;

  try {
    const raw = localStorage.getItem(key);
    const record = raw ? JSON.parse(raw) : null;
    // A record with no chat id is nothing to resume — treat it as absent.
    return record?.chatId ? record : null;
  }
  catch {
    // Unreadable or blocked storage (private mode, half-written JSON): the slot starts a
    // fresh conversation, which is what it did before keepState existed.
    return null;
  }

}

export const readChatState = (key, token = null) => {

  const record = readRecord(key);
  if (!record) return null;

  // The app moved on to another conversation while this slot kept its name, so what is
  // stored belongs to the previous one and must not be resumed.
  if ((record.token ?? null) !== token) {
    clearChatState(key);
    return null;
  }

  return record;

}

export const writeChatState = (key, token, patch) => {

  if (!key) return;

  try {
    localStorage.setItem(key, JSON.stringify({ ...(readRecord(key) || {}), ...patch, token }));
  }
  catch {
    // Out of quota or storage denied: the conversation runs, it just will not be
    // resumable on the next load.
  }

}

export const clearChatState = (key) => {

  if (!key) return;

  try {
    localStorage.removeItem(key);
  }
  catch {
    // See writeChatState — nothing here is worth failing the chat over.
  }

}
